/* eslint-disable no-unused-vars */

// Класс для объектов, которые показывают движение персонажа (по краям дороги, полосы на дороге и тп)
// Перемещаются по заданному вектору с переданной скоростью и ускорением главного персонажа игры
// Дополнительно есть собственное ускорение (сначала едут медленнее, затем ускоряются)

// Настройки для этих (и других) объектов хранятся в файле gameObjectsSettings
// При создании класса нужно передать тип объекта
// Перед началом движения вызвать метод init (передать пройденное время, скорость и область видимости игры)
// Для обновления позиции и скейла вызвать метод update (передать пройденное время игры)

import AbstractMovingObject from '../abstract/abstractMovingObject';
import Vector2d from '../helpers/vector2d';
import gameSettings, { incrementPxToCoord, convergencePointY, getIsGamePt } from '../constants/gameSettings';
import gameObjectsSettings from '../constants/gameObjectsSettings/gameObjectsSettings';
import {
    getRandomIntInclusive, getEquationOfLineFrom2Points, getTwoVectorsAngleCos, getVectorOnVectorProjection
} from '../helpers/mathMethods';
import gameObjectTypes from '../constants/gameObjectTypes';

export default class MovableObjectModel extends AbstractMovingObject {
    #type;
    get type() {
        return this.#type;
    }

    #visibleAreaWidthFromCenterOfGame = 0;
    #isPt;
    // Вектор перемещения
    #objectVelocityVector;
    // Вектор скорости (единичный вектор направления движения)
    #objectUnionVelocityVector;
    //
    #playerMovementVector;

    // вектор из начала координат игры к началу координат объекта
    #objectStartVectorInGame;

    #lineEquationScaleFromY;

    #startPoint;
    get startPoint() {
        return this.#startPoint;
    }

    endPoint;

    // x, y в системе координат объекта, где начало координат - это начальное положение объекта
    x;
    y;
    scale;

    // Сейчас мне не очень нравится реализация проверки на выход за зону видимости, в идеале доработать этот момент
    get isObjectOutOfVisibleArea() {
        return this.currentYInGame < -20 || Math.abs(this.currentXInGame) > window.innerWidth / 2;

        // const currentX = (this.x * this.#visibleAreaWidthFromCenterOfGame) / (gameSettings.roadWidthPx * 0.5 * incrementPxToCoord);
        // const currentY = (this.y * this.#visibleAreaWidthFromCenterOfGame) / (gameSettings.roadWidthPx * 0.5 * incrementPxToCoord);
        // const startX = (this.#objectStartVectorInGame.x * this.#visibleAreaWidthFromCenterOfGame)
        //                 / (gameSettings.roadWidthPx * 0.5 * incrementPxToCoord);

        // eslint-disable-next-line
        // return Math.abs(currentX) - gameObjectsSettings[this.#type].spacePx * incrementPxToCoord > this.#visibleAreaWidthFromCenterOfGame + Math.abs(startX)
        //        || currentY + gameObjectsSettings[this.#type].spacePx * incrementPxToCoord < 0 - this.#objectStartVectorInGame.y;
    }

    get currentXInGame() {
        return (this.x || 0) + this.startPoint.x * incrementPxToCoord;
    }

    get currentYInGame() {
        return (this.y || 0) + this.startPoint.y * incrementPxToCoord;
    }

    constructor(type) {
        super();

        this.#type = type;
        this.#isPt = getIsGamePt();

        // this.setStartAndEndPoints();
        this.applyPositionRandomness();
        this.setObjectVectorInGame();
        this.setObjectVelocityVectors();
        this.setPlayerMovementVector();
        this.setLineEquationScaleFromY();
    }

    setStartAndEndPoints() {
        if (this.#isPt) {
            this.#startPoint = { ...gameObjectsSettings[this.#type].startPointPt };
            this.endPoint = { ...gameObjectsSettings[this.#type].endPointPt };
        } else {
            this.#startPoint = { ...gameObjectsSettings[this.#type].startPoint };
            this.endPoint = { ...gameObjectsSettings[this.#type].endPoint };
        }
    }

    applyPositionRandomness() {
        this.setStartAndEndPoints();
        this.randomModX = 0;

        const { startPointRandomness } = gameObjectsSettings[this.#type];

        if (!startPointRandomness) {
            return;
        }

        const { minX, maxX } = startPointRandomness;

        this.randomModX = getRandomIntInclusive(minX, maxX);

        this.#startPoint.x += this.randomModX;
        this.endPoint.x += this.randomModX * 2;
    }

    updateGameSizes({ width }) {
        this.#isPt = getIsGamePt();
        this.#visibleAreaWidthFromCenterOfGame = width * 0.5 * incrementPxToCoord;
        this.setLineEquationScaleFromY();
    }

    setObjectVectorInGame = () => {
        this.#objectStartVectorInGame = new Vector2d(
            this.#startPoint.x,
            this.#startPoint.y
        ).multiplyingByNumber(incrementPxToCoord);
    };

    setPlayerMovementVector = (y = 0) => {
        if (!this.#playerMovementVector) {
            this.#playerMovementVector = new Vector2d(
                0,
                y
            ).multiplyingByNumber(incrementPxToCoord);
        } else {
            this.#playerMovementVector.y = y;
        }

    };

    // TODO можно добавить учет положения велосипедиста, в этой точке scale = 1
    setLineEquationScaleFromY() {
        this.#lineEquationScaleFromY = getEquationOfLineFrom2Points([
            0,
            this.#startPoint.scale
        ], [
            this.#objectVelocityVector.y,
            this.endPoint.scale
        ]);
    }

    init() {
        this.setStartKinematicsParams({
            startDistance: 0
        });

        this.y = 0;
        this.x = 0;

        this.applyPositionRandomness();
        this.setObjectVectorInGame();
        this.setObjectVelocityVectors();
        this.setPlayerMovementVector();
        this.setLineEquationScaleFromY();
    }

    setObjectVelocityVectors() {
        this.#objectVelocityVector = new Vector2d(
            this.endPoint.x - this.#startPoint.x,
            this.endPoint.y - this.#startPoint.y
        ).multiplyingByNumber(incrementPxToCoord);

        // console.log(this.type, this.endPoint.x, this.endPoint.y);

        this.#objectUnionVelocityVector = this.#objectVelocityVector.unitVector;

        // console.log(
        //     this.#type,
        //     this.#objectVelocityVector.x,
        //     this.#objectVelocityVector.y,
        //     this.#objectUnionVelocityVector.x,
        //     this.#objectUnionVelocityVector.y
        // );
    }

    // eslint-disable-next-line class-methods-use-this
    getHalfRoadWidthAtY(yCoord) {
        // const roadSize = this.#isPt ? gameSettings.figmaRoadSizePt : gameSettings.figmaRoadSize;
        const roadSize = gameSettings.figmaRoadSize;

        const fullRoadHeightCoord = convergencePointY * incrementPxToCoord;
        const halfRoadWidthBotCoord = (roadSize.botWidthPx / 2) * incrementPxToCoord;

        // console.log(fullRoadHeightCoord, yCoord);

        return (halfRoadWidthBotCoord * (fullRoadHeightCoord - yCoord)) / fullRoadHeightCoord;
    }

    getCurrentDistanceModifier() {
        if (this.endPoint.x === 0) return 1;

        // Костыль, нет времени сделать лучше
        if (this.#type === gameObjectTypes.LOOT_OBJECT) {
            return 1.75;
        }

        const halfRoadWidthAtObjectPosition = this.getHalfRoadWidthAtY(this.currentYInGame);

        const halfRoadWidthBotCoord = Math.abs(this.endPoint.x) * incrementPxToCoord;

        const modifier = halfRoadWidthAtObjectPosition / halfRoadWidthBotCoord;

        return modifier;
    }

    update(deltaDistance) {
        this.setPlayerMovementVector(-deltaDistance);

        const distanceModifier = this.getCurrentDistanceModifier();

        // const projection = getVectorOnVectorProjection(this.#playerMovementVector, this.#objectVelocityVector);
        const angleCos = getTwoVectorsAngleCos(this.#playerMovementVector, this.#objectVelocityVector);
        const angleMod = Math.abs(angleCos);
        const lootObjectDistanceMod = this.#isPt ? 2 : 1.5;
        const distanceMod = this.#type === gameObjectTypes.LOOT_OBJECT ? lootObjectDistanceMod : angleMod;

        const distanceToAdd = (deltaDistance / distanceMod) * distanceModifier;

        this.addDistance(distanceToAdd);
        this.moveObjectByVelocityVector();
    }

    moveObjectByVelocityVector() {
        const distanceVector = this.#objectUnionVelocityVector.multiplyingByNumber(Math.max(this.distance, 0));

        const { x, y } = distanceVector;

        this.scale = this.#lineEquationScaleFromY.k * y + this.#lineEquationScaleFromY.b;
        // Нужно, потому что когда scale очень большой (как на разделительной полосе на дороге)
        // из-за него ускорение не покрывает scale и объекты догоняют друг друга
        // TODO мб сделать более замысловатую логику... особенно если будут наблюдаться баги в скорости

        const scaleValue = gameObjectsSettings[this.#type].powRelativeFromScale ? this.scale ** 1.5 : this.scale;

        if (gameObjectsSettings[this.#type].isCenteredVelocityVector) {
            this.x = 0;
        } else {
            this.x = x * scaleValue;
        }

        this.y = y * scaleValue;
    }
}
